import uuidv5 from "uuid/v5";

/**
 * Inspiration for this module: We have a growing list of places where we are
 * calculating UUIDs based on UUIDs of other assets in the system. We need a
 * single file that tracks where we are doing this so we can be consistent about
 * how we calculate this UUIDs. */

const UUID5_TRANSLATION_ES = "__TRANSLATION_ES__";

/**
 * Calcualate UUID for translated course.
 *
 * WARNING: This is flawed because the string assumes its only ever being
 * translated to one language (ES).
 *
 * See: https://www.notion.so/examspark/Fix-bad-GUIDs-on-translated-SS-documents-273749299c8342c3bf3d98f4eb3cae42?pvs=4
 */
export const getTranslatedAssetUUID = (assetId: string) => {
  return uuidv5(UUID5_TRANSLATION_ES, assetId);
};

/**
 * Calculate Spanish-translated objective ID.
 *
 * WARNING: This is flawed because the string assumes its only ever being
 * translated to one language (ES).
 *
 * See:
 * https://www.notion.so/examspark/Fix-bad-GUIDs-on-translated-SS-documents-273749299c8342c3bf3d98f4eb3cae42?pvs=4
 *
 * Looking for shape like `ss:2022`, for example. */
export const getSpanishTranslatedObjectiveId = (
  /** The document ID of the english-language document */
  documentId: string,
  originalObjectiveId: string,
) => {
  if (documentId.includes("#"))
    throw new Error(`Invalid document ID: ${documentId}`);
  if (documentId.split(":").length !== 2)
    throw new Error(`Invalid document ID: ${documentId}`);

  return uuidv5(documentId, originalObjectiveId);
};

/**
 * Used to generate a stable id for an asset being copied from one workspace to
 * another (syncing Ignite item bank from tx-region-10)
 */
export const getStableIdForCopiedWorkspaceAsset = (
  targetWorkspaceId: string,
  sourceItemId: string,
) => {
  return uuidv5(targetWorkspaceId, sourceItemId);
};

export const getBankDuplicatedId = (uuid: string, newBankId: string) => {
  return uuidv5(`__BANK__${newBankId}`, uuid);
};

/**
 * Given a workspaceId, and title, generate a UUID for a course that is
 * imported from an external source
 */
export const getStableImportedCourseId = (
  workspaceId: string,
  title: string,
) => {
  return uuidv5(
    [workspaceId, title].join(""),
    "a1a89240-6a28-4eb9-beee-a26eb83b40df",
  );
};

/** Given a workspaceId, and title, generate a UUID for a course that is imported from an external source */
export const getStableImportedUnitId = (
  workspaceId: string,
  courseId: string,
  title: string,
) => {
  return uuidv5(
    [workspaceId, courseId, title].join(""),
    "308c6678-9a39-41c4-b693-2dd1ba1dab79",
  );
};

/** Given a Workspace ID and GetMarked question ID, return a stable item UUID. */
export const getStableGetMarkedImportedItemId = (
  workspaceId: string,
  questionId: string,
) => {
  return uuidv5(
    [workspaceId, questionId].join(""),
    "9aebf6b9-516f-4965-ad6f-f01aba1072bf",
  );
};

/** Given a Workspace ID and GetMarked passage text, return a stable passage UUID. */
export const getStableGetMarkedImportedPassageId = (
  workspaceId: string,
  passageText: string,
) => {
  // remove all url src from text
  passageText = passageText.replace(/src="([^"]*)"/g, 'src=""');

  return uuidv5(
    [workspaceId, passageText].join(""),
    // accidently ran gr 3/5 math with this namespace don't care about this
    // after 10/1/2024
    // "9aebf6b9-516f-4965-ad6f-f01aba1072bf"
    "6c324b20-fbb3-4774-8224-15f08e523920",
  );
};

/** Given a Workspace ID and filename, return a stable exam UUID. */
export const getStableGetMarkedImportedExamId = (
  workspaceId: string,
  filename: string,
) => {
  return uuidv5(
    [workspaceId, filename].join(""),
    "e37b1fed-f8d8-4008-8698-c3941a6d6bb0",
  );
};

/*
  These methods are for the Riverside 'WebCMS' import. They are used to generate
  stable UUIDs for imported items, passages, and exams. The namespace GUIDs are
  arbitrary and are not important to the UUID generation as long as they 
  are static / unique. This UUID is then used to deduplicate / overwrite 
  existing items in the bank on import.
*/
export const getStableRiversideImportedItemId = (
  riversideItemId: string,
  workspaceId: string,
  bankId: string,
) => {
  return uuidv5(
    [riversideItemId, workspaceId, bankId].join(""),
    "f3ace013-2d4b-4e6d-a972-770de31f9be0",
  );
};

export const getStableRiversideImportedPassageId = (
  riversidePassageId: string,
  workspaceId: string,
  bankId: string,
) => {
  return uuidv5(
    [riversidePassageId, workspaceId, bankId].join(""),
    "296cb562-26bb-4d05-89b6-c480b425e657",
  );
};

export const getStableRiversideImportedExamId = (
  riversideExamId: string,
  workspaceId: string,
  bankId: string,
) => {
  return uuidv5(
    [riversideExamId, workspaceId, bankId].join(""),
    "3344a3da-0eab-4782-ad20-cd2c9a64ba78",
  );
};

export const getDuplicatedAssetUUID = (
  assetId: string,
  duplicationKey: string,
) => {
  return uuidv5(`__DUPLICATE__${duplicationKey}`, assetId);
};

export const getStablePcgResourceId = (
  sk: string,
  workspaceId: string,
  stableId: string,
) => {
  return uuidv5(
    [sk, workspaceId, stableId].join(""),
    "3344a3da-0eab-4782-ad20-cd2c9a64ba78",
  );
};

export const getStableObjectiveId = (
  authorityId: string,
  publicationId: string,
  documentId: string,
  sectionId: string,
  /** Could be a machine generated ID or abbreviation */
  objectiveId: string,
) => {
  return uuidv5(
    [authorityId, publicationId, documentId, sectionId, objectiveId].join(""),
    "6147175b-7bd7-421d-ad86-ff8cfb7c4f44",
  );
};

/**
 You almost certainly should not be using this
 outside of the API packages. If you need an exam attempt ID,
 get it from the database, don't compute it yourself.
*/
export const getStableExamAttemptId = (
  examSessionId: string,
  attemptNumber: number,
) => {
  return uuidv5(attemptNumber.toString(), examSessionId);
};

export const getStableLaunchTokenId = (token: string) => {
  return uuidv5(
    token,
    "26019744-5728-41d9-bac7-e459e465e5e0" /* <- this was randomly chosen */,
  );
};

const GOOGLE_CLASSROOM_USER_ID_HASHING_GUID =
  "0fb3a758-c98c-43cb-b4cd-48baa71c7bc4";

export const getStableExternalUserId = (
  providerUserId: string,
  providerName: string,
) => {
  return uuidv5(
    `${providerName}:${providerUserId.toLowerCase()}`,
    "f87affda-f676-43f9-86df-1790fb09ca7c",
  );
};

/**
 * Generates a deterministic student id that will be unique to a classroom.
 *
 * Specifying `service` allows for additional hashing to ensure rostering
 * services do not inadvertently hash into values that are equivalent.
 */
export const getStableClassroomStudentId = (
  /** Must be a GUID for hashing purposes */
  classroomId: string,
  /** This should be the student ID as it exists in an SIS. */
  studentUserId: string,
  options?: {
    service?: "google-classroom";
    /**
     * B/C support for legacy classrooms. Student ID seeds in new UUID v5's will
     * use `toLowerCase()` to ensure case-insensitivity. */
    caseSensitive?: boolean;
  },
) => {
  if (!options?.caseSensitive) {
    studentUserId = studentUserId.toLowerCase();
  }

  /**
   * If this is a google-rostered student, we can just create v5 UUID with a
   * stable GUID as the seed. We don't need to use classroom ID as the hash. */
  if (options?.service === "google-classroom") {
    studentUserId = uuidv5(
      studentUserId,
      GOOGLE_CLASSROOM_USER_ID_HASHING_GUID,
    );
  }

  const id = uuidv5(studentUserId, classroomId);
  return id;
};

export const getStableSingleBankProductId = (bankId: string) => {
  const id = uuidv5(bankId, "f58f0dc4-30a1-46cb-a28e-389a41f83a2e");
  return id;
};

export const getStableSingleCourseProductId = (courseId: string) => {
  const id = uuidv5(courseId, "7edd536a-c149-49c0-a79e-7f8b59e9163f");
  return id;
};
